import { useState } from 'react'

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { InvitePayeeFormValues } from '~/components/compliance/PayeeList'
import { QUERY_KEYS } from '~/constants/react-query'
import { REFETCH_ORG_COMPLIANCE } from '~/constants/refetch-intervals'
import { usePayeeLookupById } from '~/hooks/compliance/usePayeeLookup'
import { useActiveOrg, useActiveOrgId, useActiveOrgMember } from '~/hooks/org'
import { useAuthUser } from '~/hooks/useAuthUser'
import { useQueryWrapper } from '~/hooks/useQueryWrapper'
import { useServerRequests } from '~/hooks/useRequests'
import { CompliantPaymentRelation, OrgComplianceData } from '~/types'
import { EvmAddress } from '~/utils/address'
import { openBlob } from '~/utils/files'

export function useOrgCompliance({
  enabled = true,
}: { enabled?: boolean } = {}) {
  const { org: activeOrg, featureFlags } = useActiveOrg()
  const { serverGetRequest } = useServerRequests()

  const isFeatureFlagEnabled = !!featureFlags?.['compliance']

  const query = useQueryWrapper<{ compliance: OrgComplianceData | undefined }>({
    queryKey: [QUERY_KEYS.ORG_COMPLIANCE, activeOrg?.id],
    queryFn: async () => {
      if (!activeOrg?.id) throw new Error('Org ID required')

      const result = await serverGetRequest<{
        compliance: OrgComplianceData | undefined
      }>({
        path: '/api/org/compliance',
        params: { orgId: activeOrg.id },
      })

      if (!result) {
        throw new Error('No data returned')
      }

      return result.data
    },
    refetchInterval: REFETCH_ORG_COMPLIANCE,
    enabled: !!activeOrg?.id && enabled,
  })

  const complianceData = query.data?.compliance
  const numPayers = complianceData?.payers?.length ?? 0

  return {
    canViewPage: isFeatureFlagEnabled || numPayers > 0,
    canAddRecipients: isFeatureFlagEnabled, // TODO: require to be US based
    isVerified: complianceData?.isVerified,
    payees: complianceData?.payees ?? [],
    payers: complianceData?.payers ?? [],
    query,
    isLoading: query.isLoading,
  }
}

export function usePayerInvitations() {
  const { serverGetRequest } = useServerRequests()
  const { user: authUser } = useAuthUser()

  return useQuery<{ invitations: CompliantPaymentRelation[] }>({
    queryKey: [QUERY_KEYS.PAYER_INVITATIONS, authUser?.id],
    queryFn: async () => {
      if (!authUser?.id) throw new Error('User ID required')

      const result = await serverGetRequest<{
        invitations: CompliantPaymentRelation[]
      }>({
        path: '/api/user/compliance/payer/invitations',
      })

      if (!result) {
        throw new Error('No data returned')
      }

      return result.data
    },
    enabled: !!authUser,
    refetchInterval: REFETCH_ORG_COMPLIANCE,
  })
}

export function useInvitePayee() {
  const activeOrgId = useActiveOrgId()
  const { serverPostRequest, isProcessing } = useServerRequests()
  const queryClient = useQueryClient()

  const invitePayee = async (
    formData: InvitePayeeFormValues,
    onSuccess: () => void,
  ) => {
    if (!activeOrgId) throw new Error('Org ID required')
    const { email, address, chainIds } = formData

    if (address && !EvmAddress.validate(address)) {
      toast.error('Unsupported address', {
        description: 'Compliance does not support non-EVM addresses',
      })
      return
    }

    const result = await serverPostRequest({
      path: '/api/org/compliance/payee/invite',
      params: {
        payerOrgId: activeOrgId,
        payeeEmail: email,
        address,
        chainIds,
      },
      errorMessage: 'Failed to invite recipient',
      successMessage: 'Sent invitation email to recipient',
    })

    if (result) {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORG_COMPLIANCE, activeOrgId],
      })
      onSuccess()
    }
  }

  return { invitePayee, isProcessing }
}

export function useResendPayeeInvitation() {
  const activeOrgId = useActiveOrgId()
  const { serverPutRequest, isProcessing } = useServerRequests()
  const queryClient = useQueryClient()

  const resendPayeeInvitation = async (
    compliantPaymentRelationId: string,
    onSuccess: () => void,
  ) => {
    if (!activeOrgId) throw new Error('Org ID required')
    const result = await serverPutRequest({
      path: `/api/org/compliance/payee/invite/${compliantPaymentRelationId}/resend`,
      errorMessage: 'Failed to resend invitation email',
      successMessage: 'Resent invitation email to recipient',
    })

    if (result) {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORG_COMPLIANCE, activeOrgId],
      })
      onSuccess()
    }
  }

  return { resendPayeeInvitation, isProcessing }
}

export function useRevokePayeeInvitation() {
  const activeOrgId = useActiveOrgId()
  const { serverDeleteRequest, isProcessing } = useServerRequests()
  const queryClient = useQueryClient()

  const revokePayeeInvitation = async (
    compliantPaymentRelationId: string,
    onSuccess: () => void,
  ) => {
    if (!activeOrgId) throw new Error('Org ID required')
    const result = await serverDeleteRequest({
      path: `/api/org/compliance/payee/invite/${compliantPaymentRelationId}`,
      errorMessage: 'Failed to revoke invitation',
      successMessage: 'Revoked invitation',
    })

    if (result) {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORG_COMPLIANCE, activeOrgId],
      })
      onSuccess()
    }
  }

  return { revokePayeeInvitation, isProcessing }
}

export function usePayeeTaxCertificate(
  compliantPaymentRelationId: string | undefined,
) {
  const { serverGetRequest } = useServerRequests()

  const [isDownloading, setIsDownloading] = useState(false)

  const activeOrgId = useActiveOrgId()

  const downloadDocument = async () => {
    if (!activeOrgId) throw new Error('Org ID required')
    if (!compliantPaymentRelationId)
      throw new Error('Compliant payment relation ID required')

    setIsDownloading(true)
    const result = await serverGetRequest<Blob>({
      path: '/api/org/compliance/payee/tax-certificate/document',
      params: {
        orgId: activeOrgId,
        compliantPaymentRelationId,
      },
      errorMessage: 'Failed to generate tax certificate',
      responseType: 'blob',
    })

    if (result) {
      openBlob(result.data)
    }

    setIsDownloading(false)
  }

  return { downloadDocument, isDownloading }
}

export function usePayee1099(
  compliantPaymentRelationId: string | undefined,
  filingYear: number,
) {
  const { serverGetRequest } = useServerRequests()

  const [isDownloading, setIsDownloading] = useState(false)

  const activeOrgId = useActiveOrgId()
  const activeMemberRole = useActiveOrgMember()?.role

  const { payees } = useOrgCompliance()
  const payee = payees.find((p) => p.id === compliantPaymentRelationId)
  const { isVerified } = usePayeeLookupById(payee?.id)

  const enabled =
    activeMemberRole === 'OWNER' && !!compliantPaymentRelationId && !!isVerified

  const downloadDocument = async () => {
    if (!activeOrgId) throw new Error('Org ID required')
    if (!compliantPaymentRelationId)
      throw new Error('Compliant payment relation ID required')

    setIsDownloading(true)

    const result = await serverGetRequest<Blob>({
      path: '/api/org/compliance/payee/1099/document',
      params: {
        orgId: activeOrgId,
        compliantPaymentRelationId,
        filingYear,
      },
      errorMessage: 'Failed to generate 1099 form',
      responseType: 'blob',
    })

    if (result) {
      openBlob(result.data)
    }

    setIsDownloading(false)
  }

  return {
    downloadDocument,
    enabled,
    isDownloading,
  }
}

export type Payee1099Year = {
  filingYear: number
  usdTotal: string
}

export function usePayee1099Years(
  compliantPaymentRelationId: string | undefined,
) {
  const { serverGetRequest } = useServerRequests()
  const activeOrgId = useActiveOrgId()
  const activeMemberRole = useActiveOrgMember()?.role

  return useQuery<{ years: Payee1099Year[] }>({
    queryKey: [
      QUERY_KEYS.PAYEE_1099,
      'years',
      activeOrgId,
      compliantPaymentRelationId,
    ],
    queryFn: async () => {
      if (!activeOrgId) throw new Error('Org ID required')
      if (!compliantPaymentRelationId)
        throw new Error('Compliant payment relation ID required')

      const result = await serverGetRequest<{ years: Payee1099Year[] }>({
        path: '/api/org/compliance/payee/1099/years',
        params: { orgId: activeOrgId, compliantPaymentRelationId },
        errorMessage: 'Failed to load 1099 filing years',
      })

      if (!result) throw new Error('No data returned')
      return result.data
    },
    enabled:
      activeMemberRole === 'OWNER' &&
      !!activeOrgId &&
      !!compliantPaymentRelationId,
  })
}
